import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const BrandingContext = createContext({});

export const useBranding = () => useContext(BrandingContext);

// localStorage key for the last-known branding. We hydrate from this on
// boot so a refresh doesn't flash the hardcoded defaults before the
// API call completes. Bump the version suffix whenever DEFAULT_BRANDING
// changes meaningfully so existing visitors don't keep seeing the
// previous palette out of cache.
// v5: header default re-tuned to the Khulja logo's steel navy
// (#1e4d8c). Bumping the version invalidates any cache that still
// pins header_bg_color to the previous value.
const BRANDING_CACHE_KEY = 'wms_branding_cache_v5_logo_navy';

// Brand defaults — Khulja Sim Sim white + purple palette. These ship as
// the absolute fallback (first-ever visit, no cache, API unreachable).
// Super-admin overrides at /settings/branding take precedence; the cache
// layer below makes those overrides stick across refreshes without a flash.
const DEFAULT_BRANDING = {
  platform_name: 'Khulja Sim Sim',
  platform_tagline: 'Warehousing Made Easy',
  platform_description: 'List, discover, book, and manage warehousing space with ease.',
  logo_url: '/logo-icon.png',
  logo_wordmark_url: '/logo-wordmark.png',
  favicon_url: '/favicon.ico',
  app_version: '2.0.0',
  footer_copyright: 'Copyright 2026 Khulja Sim Sim. All Rights Reserved.',
  footer_privacy_url: '/privacy',
  footer_terms_url: '/terms',
  // Violet primary on a white canvas. Chosen to read clearly on white
  // chrome and produce sufficient contrast for white text on buttons.
  primary_color: '#7C3AED',
  secondary_color: '#5B21B6',
  accent_color: '#A78BFA',
  auth_gradient_start: '#7C3AED',
  auth_gradient_end: '#4C1D95',
  sidebar_bg_color: '#1f1633',
  sidebar_text_color: '#ffffff',
  sidebar_active_color: '#7C3AED',
  // Matches the Khulja logo's steel navy so the navbar reads as
  // one continuous brand surface with the wordmark.
  header_bg_color: '#1e4d8c',
  header_text_color: '#ffffff',
  footer_bg_color: '#ffffff',
  footer_text_color: '#666666',
  body_bg_color: '#ffffff',
  button_color: '#7C3AED',
  button_hover_color: '#5B21B6',
  link_color: '#6D28D9',
  modal_header_bg: '#7C3AED',
  modal_header_text: '#ffffff',
  card_border_color: '#ede9fe',
  success_color: '#10b981',
  danger_color: '#ef4444',
  warning_color: '#f59e0b',
  support_email: 'support@khuljasimsim.in',
  support_phone: '+91 98765 43210',
  support_whatsapp: '+91 98765 43210',
  social_facebook: '',
  social_twitter: '',
  social_linkedin: '',
  social_instagram: '',
  auth_stat_warehouses: '500+',
  auth_stat_warehouses_label: 'Warehouses',
  auth_stat_cities: '50+',
  auth_stat_cities_label: 'Cities',
  auth_stat_area: '10M+',
  auth_stat_area_label: 'Sq.Ft',
  meta_title: 'Khulja Sim Sim — Warehousing Made Easy',
  meta_description: 'India\'s B2B warehouse marketplace. Search, compare, and book verified warehouse space across 50+ cities.',

  // DPDP §13 — Data Protection Officer contact must be discoverable
  // (footer + grievance form). Admin overrides via /settings/branding.
  dpo_name: 'Data Protection Officer',
  dpo_email: 'dpo@khuljasimsim.in',
  dpo_phone: '',
};

const readCachedBranding = () => {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch { /* ignore corrupt cache */ }
  return null;
};

export const BrandingProvider = ({ children }) => {
  // Initialise SYNCHRONOUSLY from the cache when present — this is the
  // key change that kills the flash. ``useState`` runs the initializer
  // once on first render, so by the time the page paints we already
  // have the saved orange theme in memory.
  const [branding, setBranding] = useState(() => {
    const cached = readCachedBranding();
    return { ...DEFAULT_BRANDING, ...(cached || {}) };
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // This is a public endpoint - no auth needed
        const { data } = await api.get('/master-data/branding');
        // Flatten grouped response: {general: {...}, theme: {...}} -> flat dict
        const flat = {};
        if (data && typeof data === 'object') {
          Object.values(data).forEach((group) => {
            if (typeof group === 'object' && group !== null && !Array.isArray(group)) {
              Object.assign(flat, group);
            }
          });
          if (Object.keys(flat).length === 0) Object.assign(flat, data);
        }
        setBranding((prev) => {
          const next = { ...prev, ...flat };
          // Persist so the next refresh hydrates straight into these
          // colours without a flash. Best-effort — quota / privacy mode
          // failures are silent.
          try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(next)); }
          catch { /* ignore */ }
          return next;
        });
      } catch {
        // Use defaults silently
      } finally {
        setLoaded(true);
      }
    };
    fetchBranding();
  }, []);

  // Apply dynamic CSS custom properties for theme colors
  useEffect(() => {
    const root = document.documentElement;
    const set = (prop, val) => { if (val) root.style.setProperty(prop, val); };

    // Sidebar
    set('--wms-sidebar-bg', branding.sidebar_bg_color);
    set('--wms-sidebar-text', branding.sidebar_text_color);
    set('--wms-sidebar-active', branding.sidebar_active_color);
    // Header
    set('--wms-header-bg', branding.header_bg_color);
    set('--wms-header-text', branding.header_text_color);
    // Footer
    set('--wms-footer-bg', branding.footer_bg_color);
    set('--wms-footer-text', branding.footer_text_color);
    // Body
    set('--wms-body-bg', branding.body_bg_color);
    // Buttons & Links
    set('--wms-button', branding.button_color);
    set('--wms-button-hover', branding.button_hover_color);
    set('--wms-link', branding.link_color);
    // Modals
    set('--wms-modal-header-bg', branding.modal_header_bg);
    set('--wms-modal-header-text', branding.modal_header_text);
    // Cards
    set('--wms-card-border', branding.card_border_color);
    // Status
    set('--wms-success', branding.success_color);
    set('--wms-danger', branding.danger_color);
    set('--wms-warning', branding.warning_color);
    // Login page gradient
    set('--wms-gradient-start', branding.auth_gradient_start);
    set('--wms-gradient-end', branding.auth_gradient_end);

    // Update page title — but only the site-default. A route that set its
    // own title (via useDocumentMeta / a page effect) marks it locked, so
    // branding (which loads async) doesn't clobber per-page SEO titles.
    if (branding.meta_title && !window.__routeTitleSet) {
      document.title = branding.meta_title;
    }
  }, [branding]);

  // Function to refresh branding (called after admin saves)
  const refreshBranding = async () => {
    try {
      const { data } = await api.get('/master-data/branding');
      const flat = {};
      Object.values(data).forEach((group) => {
        if (typeof group === 'object' && group !== null && !Array.isArray(group)) {
          Object.assign(flat, group);
        }
      });
      if (Object.keys(flat).length === 0) Object.assign(flat, data);
      setBranding((prev) => {
        const next = { ...prev, ...flat };
        try { localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(next)); }
        catch { /* ignore */ }
        return next;
      });
    } catch { /* silent */ }
  };

  return (
    <BrandingContext.Provider value={{ ...branding, loaded, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
